import React, { useCallback, useRef, useState } from 'react';
import { Button, DatePicker, Modal, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18n';
import { useToggle } from 'react-use';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { getDailyStatistics } from 'apis/report';
import { DailyStatisticsType } from 'types/report';
import { thousands } from 'utils/tools';
import exportToExcel from 'utils/exportToExcel';
import SalesManagementTable, {
  SalesManagementTableRef,
} from './SalesManagementTable';
import PurchaseDailyReport, {
  PurchaseDailyReportRef,
} from 'pages/report/statement/components/PurchaseDailyReport';
import ConsignmentDailyReport, {
  ConsignmentDailyReportRef,
} from 'pages/report/statement/components/ConsignmentDailyReport';
import { SHOP_MAP } from 'constant/shop';

interface Props {
  selectedShopId?: number;
}

const DailyReportStatement: React.FC<Props> = ({ selectedShopId }) => {
  const [loading, toggleLoading] = useToggle(false);
  const [dailyData, setDailyData] = React.useState<DailyStatisticsType>();
  const purchaseRef = useRef<PurchaseDailyReportRef>(null);
  const consignmentRef = useRef<ConsignmentDailyReportRef>(null);
  const salesRef = useRef<SalesManagementTableRef>(null);

  const { staffSelectOptions } = useAppSelector(selectGlobalInfo);

  const [rangePickerOpen, toggleRangePickerOpen] = useToggle(false);
  const [rangePickerValue, setRangePickerValue] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('day'),
    dayjs().endOf('day'),
  ]);

  const fetchData = useCallback(
    (date?: string, createdFrom?: number) => {
      const toDay = dayjs().startOf('day').format();
      toggleLoading(true);
      getDailyStatistics({ date: date || toDay, createdFrom })
        .then((res) => {
          res.orderStatistics.orders.forEach((order) => {
            if (!order.staffName && order.createdBy) {
              order.staffName =
                staffSelectOptions.find((d) => d.value === order.createdBy)
                  ?.label || '';
            }
          });

          // sort by staffName desc to match Statement behavior
          res.orderStatistics.orders.sort((a, b) =>
            (b.staffName || '').localeCompare(a.staffName || ''),
          );

          setDailyData(res);
        })
        .finally(() => toggleLoading(false));
    },
    [staffSelectOptions, toggleLoading],
  );

  React.useEffect(() => {
    const dateStr = dayjs().startOf('day').format();
    fetchData(dateStr, selectedShopId);
  }, [fetchData, selectedShopId]);

  const currencySwitch = useCallback(
    (value: number) => {
      const createdFrom = selectedShopId;
      if (createdFrom) {
        if (createdFrom === SHOP_MAP.GINZA || createdFrom === SHOP_MAP.UMEDA)
          return `JPY ${thousands(value)}`;
        if (createdFrom === SHOP_MAP.HONGKONG) return `HKD ${thousands(value)}`;
        if (
          createdFrom === SHOP_MAP.SINGAPORE ||
          createdFrom === SHOP_MAP.SINGAPORE_ASU_GST ||
          createdFrom === SHOP_MAP.SINGAPORE_GX
        )
          return `SGD ${thousands(value)}`;
        if (createdFrom === SHOP_MAP.SHANG_HAI)
          return `CNY ${thousands(value)}`;
        if (createdFrom === SHOP_MAP.DUBAI) return `AED ${thousands(value)}`;
        if (createdFrom === SHOP_MAP.NEW_YORK) return `USD ${thousands(value)}`;
      }
      return `JPY ${thousands(value)}`;
    },
    [selectedShopId],
  );

  const dlExcel = useCallback(
    async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
      if (staffSelectOptions.length === 0 && dailyData?.orderStatistics?.orders)
        return;

      const salesSheetData = (salesRef.current &&
        (await salesRef.current.getSheetData())) || {
        headers: [],
        rows: [],
      };
      const todayRows = (salesSheetData.rows || []).filter((row) => {
        return (
          row &&
          row[0] &&
          new Date(row[0]).getTime() >= new Date(startDate).getTime() &&
          new Date(row[0]).getTime() <= new Date(endDate).getTime()
        );
      });
      const salesSheet = {
        sheetName: i18n.t('sales_management_table'),
        data: [salesSheetData.headers, ...todayRows],
      };

      const purchaseSheetData = purchaseRef.current?.getSheetData();
      const purchaseSheet = {
        sheetName: i18n.t('purchase_daily_report'),
        data: [
          purchaseSheetData?.headers || [],
          ...(purchaseSheetData?.rows || []),
          [],
          [],
          ...(purchaseSheetData?.stats || []),
        ],
      };

      const consignmentSheetData = consignmentRef.current?.getSheetData();
      const consignmentSheet = {
        sheetName: i18n.t('consignment_daily_report'),
        data: [
          consignmentSheetData?.headers || [],
          ...(consignmentSheetData?.rows || []),
          [],
          [],
          ...(consignmentSheetData?.stats || []),
        ],
      };

      exportToExcel(
        [salesSheet, purchaseSheet, consignmentSheet],
        `${i18n.t('nikkei_table')}_${dayjs(startDate).format(
          'YYYY-MM-DD',
        )}_${dayjs(endDate).format('YYYY-MM-DD')}.xlsx`,
      );

      toggleRangePickerOpen(false);
    },
    [
      staffSelectOptions.length,
      dailyData?.orderStatistics?.orders,
      toggleRangePickerOpen,
    ],
  );

  return (
    <div className="mb-8">
      <div className="flex justify-end mb-4">
        <Space>
          <Button
            type="primary"
            onClick={() => {
              toggleRangePickerOpen(true);
            }}
          >
            {i18n.t('download_excel')}
          </Button>
        </Space>
      </div>

      <Modal
        title={i18n.t('download_excel')}
        open={rangePickerOpen}
        onCancel={() => toggleRangePickerOpen(false)}
        onOk={() => {
          dlExcel({
            startDate: rangePickerValue[0].format(),
            endDate: rangePickerValue[1].format(),
          });
        }}
      >
        <DatePicker.RangePicker
          allowClear={false}
          value={rangePickerValue}
          onChange={(values) => {
            if (values && values[0] && values[1]) {
              setRangePickerValue([
                values[0].startOf('day'),
                values[1].endOf('day'),
              ]);
            }
          }}
        />
      </Modal>

      <div className="flex flex-col gap-4">
        <SalesManagementTable
          ref={salesRef}
          createdFrom={selectedShopId}
          loading={loading}
        />

        <PurchaseDailyReport
          ref={purchaseRef}
          loading={loading}
          records={dailyData?.purchaseReport.records}
          statistics={dailyData?.purchaseReport?.statistics}
          currencySwitch={currencySwitch}
        />

        <ConsignmentDailyReport
          ref={consignmentRef}
          loading={loading}
          records={dailyData?.consignmentReport.records}
          statistics={dailyData?.consignmentReport?.statistics}
          currencySwitch={currencySwitch}
        />
      </div>
    </div>
  );
};

export default DailyReportStatement;
